import AppButton from '../../components/AppButton'
import 'react'
import './styles.scss'
import { AutocompleteTextField, NumberInputField } from '../../components/Forms/fields'
import { useTranslations } from '../../store/translations/hooks'
import React, { useState } from 'react'
import { ClientData, ClientSearch } from '../../models'
import { useGeneralSettings, usePacksSettings } from '../../store/settings/hooks'
import { InputGroup, InputGroupAddon } from 'reactstrap'
import { useDispatch, useSelector } from 'react-redux'
import { loadEntities } from '../../store/entities/actions'
import { getClientsSearchAsync } from '../../ajax/requests'
import { ClientEntity } from '../../store/entities/state'
import { TK } from '../../store/translations/translationKeys'
import { CheckInputField } from '../../components/Forms/fields/CheckInputField'
import { getEntitiesSearchItems, isLoadingEntities } from '../../store/entities/selectors'
import { CSVLink } from 'react-csv'
import { ApplicationState } from '../../store'
import { ClientsIntelligentCategorizationSettingsLink } from '../../components/ClientsIntelligentCategorizationSettingsLink'
import PublishIcon from '@material-ui/icons/Publish'
import SettingsIcon from '@material-ui/icons/Settings'
import DownloadIcon from '@material-ui/icons/GetAppRounded'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import TuneIcon from '@material-ui/icons/Tune'
import WarningIcon from '@material-ui/icons/Warning'
import CleaningServicesIcon from '@material-ui/icons/DeleteSweep'
import { push } from 'connected-react-router'
import { paths } from '../../app/constants'
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@material-ui/core'

const defaultSearch = (): ClientSearch => ({
  offset: 0,
  pageSize: 1000,
})

interface Props {
  onImportClientsClick: () => void;
  onClientProfileSettingsClick: () => void;
  isAdmin: boolean;
}

export const ClientAdvancedSearch: React.FC<Props> = ({
  onImportClientsClick,
  onClientProfileSettingsClick,
  isAdmin,
}) => {

  const t = useTranslations()
  const dispatch = useDispatch()

  const [state, setState] = useState<ClientSearch>(defaultSearch())

  const loading = useSelector(isLoadingEntities('clients'))

  const settings = useGeneralSettings()
  const packs = usePacksSettings()

  const tags = Object.keys(settings.clientTags)

  const handleSearchClick = () => {
    const fetch = () => getClientsSearchAsync(state)
    dispatch(loadEntities<ClientEntity>('clients', fetch, 'NewResult'))
  }

  const hasFilter = Object.values(state).some(v => !!v)

  return (
    <div style={{display: 'flex', flexDirection: 'column'}}>
      <div className='clientAdvancedSearchContainer'>
        <div style={{minWidth: '200px'}}>
          <AutocompleteTextField
            id='clients-advanced-search-tags'
            style={{minWidth: '200px'}}
            label={t(TK.clientTags)}
            options={tags}
            onAddNewOption={clientTag => setState({...defaultSearch(), clientTags: [clientTag]})}
            addNewLabel={t(TK.add)}
            multiSelect
            values={state.clientTags}
            onValuesChanged={clientTags => setState({...defaultSearch(), clientTags})}
          />
          <ClientsIntelligentCategorizationSettingsLink />
        </div>
        <AutocompleteTextField
          id='clients-advanced-search-pack'
          style={{minWidth: '200px'}}
          label={`${t(TK.pack)}/${t(TK.subscription)}`}
          options={[...new Set(packs.map(p => p.name))]}
          value={packs.find(p => (p.templateId ?? p.id) === state.packTemplateId)?.name}
          onValueChanged={name => setState({...defaultSearch(), packTemplateId: packs.find(p => p.name === name)?.templateId ?? packs.find(p => p.name === name)?.id})}
        />
        {state.packTemplateId && <div>
          <InputGroup>
            <NumberInputField
              id='clients-advanced-search-pack-expiring-days'
              label={t(TK.expiringIn)}
              style={{width: '7rem'}}
              disabled={!state.packTemplateId}
              value={state.packExpiringInDays}
              onChange={packExpiringInDays => setState(prev => ({...prev, packExpiringInDays}))}
            />
            <InputGroupAddon addonType="append">{t(TK.days).toLocaleLowerCase()}</InputGroupAddon>
          </InputGroup>
          <CheckInputField
            id='clients-without-pack-enabled'
            style={{minWidth: '7rem'}}
            label={t(TK.withoutPack)}
            value={state.packNotPresent}
            disabled={!state.packTemplateId}
            onChange={packNotPresent => setState(prev => ({...prev, packNotPresent}))}/>
        </div>
      }
      </div>
      <div style={{marginTop: 'var(--spacer-S)', marginBottom: 'var(--spacer-S)', display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacer-XS)', alignItems: 'center', flexWrap: 'wrap'}}>
        <AppButton color='warning' outline onClick={() => dispatch(push(`${paths.billing()}?tab=debt`))}>{t(TK.viewDebts)}</AppButton>
        <AppButton disabled={!hasFilter || loading} color='success' onClick={handleSearchClick}>{t(TK.search)}</AppButton>
      </div>
    </div>
  )
}

interface ClientActionsMenuProps {
  isAdvancedOpen: boolean;
  onToggleAdvanced: () => void;
  isAdmin: boolean;
  onImportClientsClick: () => void;
  onClientProfileSettingsClick: () => void;
  onClearDuplicatesClick: () => void;
}

export const ClientActionsMenu: React.FC<ClientActionsMenuProps> = ({
  isAdvancedOpen,
  onToggleAdvanced,
  isAdmin,
  onImportClientsClick,
  onClientProfileSettingsClick,
  onClearDuplicatesClick,
}) => {
  const t = useTranslations()
  const dispatch = useDispatch()

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  const clients = useSelector<ApplicationState, ClientData[]>(s => getEntitiesSearchItems(s, 'clients'))

  const csvHeaders = React.useMemo(() => ([
    { label: t(TK.id), key: 'id' },
    { label: t(TK.name), key: 'name' },
    { label: t(TK.identificationNumber), key: 'identificationNumber' },
    { label: t(TK.taxNumber), key: 'taxNumber' },
    { label: t(TK.healthNumber), key: 'healthNumber' },
    { label: t(TK.subsystemName), key: 'subsystemName' },
    { label: t(TK.subsystemNumber), key: 'subsystemNumber' },
    { label: t(TK.sex), key: 'sex' },
    { label: t(TK.birthdate), key: 'birthday' },
    { label: t(TK.job), key: 'job' },
    { label: t(TK.origin), key: 'origin' },
    { label: `${t(TK.address)} 1`, key: 'addressLine1' },
    { label: `${t(TK.address)} 2`, key: 'addressLine2' },
    { label: t(TK.zipCode), key: 'zipCode' },
    { label: t(TK.city), key: 'city' },
    { label: t(TK.height), key: 'height' },
    { label: t(TK.phoneNumbers), key: 'phoneNumbers' },
    { label: t(TK.email), key: 'email' },
    { label: t(TK.acceptMarketingCommunication), key: 'acceptMarktingCommunication' },
    { label: t(TK.warning), key: 'warningNote' },
    { label: t(TK.notes), key: 'notes' },
    { label: t(TK.internalNotes), key: 'internalNotes' },
    { label: t(TK.consentApproved), key: 'rgpdApproved' },
    { label: t(TK.status), key: 'status' },
    { label: t(TK.clientTags), key: 'tags' },
    { label: t(TK.visibleForAllProfessionals), key: 'visibleForAllProfessionals' },
    { label: t(TK.professional), key: 'professionalId' },
    { label: t(TK.mintuPausedUntil), key: 'mintuPausedUntil' },
    { label: t(TK.clientFileNumberLabel), key: 'clientFileNumber' },
    { label: t(TK.hasPortalAccess), key: 'hasPortalAccess' },
  ]), [t])

  const close = () => setMenuAnchor(null)

  return (
    <>
      <IconButton size='small' onClick={e => setMenuAnchor(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={close}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { close(); onToggleAdvanced(); }}>
          <ListItemIcon><TuneIcon fontSize='small' /></ListItemIcon>
          <ListItemText primary={t(TK.advancedSearch)} />
        </MenuItem>
        <MenuItem onClick={() => { close(); dispatch(push(`${paths.billing()}?tab=debt`)); }}>
          <ListItemIcon><WarningIcon fontSize='small' /></ListItemIcon>
          <ListItemText primary={t(TK.inDebt)} />
        </MenuItem>
        <MenuItem
          component={CSVLink}
          headers={csvHeaders}
          data={clients}
          separator=';'
          uFEFF
          target='_blank'
          filename='clients-export.csv'
          onClick={close}
        >
          <ListItemIcon><DownloadIcon fontSize='small' /></ListItemIcon>
          <ListItemText primary={t(TK.exportCsv)} />
        </MenuItem>
        {isAdmin && [
          <MenuItem key='import' onClick={() => { close(); onImportClientsClick(); }}>
            <ListItemIcon><PublishIcon fontSize='small' /></ListItemIcon>
            <ListItemText primary={t(TK.importClients)} />
          </MenuItem>,
          <MenuItem key='clear-duplicates' onClick={() => { close(); onClearDuplicatesClick(); }}>
            <ListItemIcon><CleaningServicesIcon fontSize='small' /></ListItemIcon>
            <ListItemText primary={t(TK.clearDuplicates)} />
          </MenuItem>,
          <MenuItem key='settings' onClick={() => { close(); onClientProfileSettingsClick(); }}>
            <ListItemIcon><SettingsIcon fontSize='small' /></ListItemIcon>
            <ListItemText primary={t(TK.clientProfileSettings)} />
          </MenuItem>,
        ]}
      </Menu>
    </>
  )
}
